/**
 * Client-side registry of query-shaping session profiles.
 *
 * A "session profile" identifies the host use case of the combined AI assistant
 * (AccentQueryShapingAssistantDlg): workflow triggers, custom validation, and future
 * hosts such as reports or dashboard widgets. The profile KEY is a shared contract with
 * the server — it is sent as `sessionProfile` on the chat request and must match a key in
 * QueryShapingSessionProfiles.All (C#), where the server-side half of the profile lives as
 * a plugin class implementing IQueryShapingSessionProfile (prompt context roots, host
 * rules, examples, result binding, and how shapes are tested).
 *
 * Responsibility split:
 *  - SERVER profile: everything the AI is told (available ctx roots, host rules, examples)
 *    plus host-specific behavior (shape testing harness, result binding, sanitization).
 *  - CLIENT profile (this file): how the dialog behaves for that host — which artifact
 *    kind outputs are tested as, whether the user may add outputs, default result shape,
 *    and the labels/hints shown around the outputs panel.
 *  - HOST props: per-instance data only (the query text, existing outputs, catalogs,
 *    variables, template values, and the result binding path when it varies per host —
 *    e.g. batch timers bind 'trigger.rows' while entity timers deliberately bind nothing).
 *
 * ADDING A PROFILE: add the server plugin first (implement IQueryShapingSessionProfile —
 * usually via QueryShapingSessionProfileBase — and register it in
 * QueryShapingSessionProfiles.All; its invariant tests verify the prompt context against
 * the shared JS harness), then
 * register the client behavior here. Hosts should configure the dialog with
 * `sessionProfile="<key>"` plus data props — never by re-specifying behavior the profile
 * already owns.
 *
 * Labels/hints are stored untranslated; the dialog runs them through t() at render time.
 */

import { AdhocReportPreview } from './AdhocReportPreview';
import { DataExportPreview } from './DataExportPreview';
import { DataGridPreview } from './DataGridPreview';
import { getAccess, getUser } from '../../services/UserService';

export const QUERY_SHAPING_DEFAULT_PROFILE = 'workflowTrigger';

export const queryShapingSessionProfiles = {
  workflowTrigger: {
    key: 'workflowTrigger',
    /** Trigger queries are SQL over the Api_* views; sent on every test request. */
    queryLanguage: 'sql',
    /** Outputs are trigger output variables, tested via the set-variable harness. */
    shapeKind: 'triggerOutput',
    /** Users (and the AI) may introduce additional output variables. */
    allowAddOutputs: true,
    /** Trigger queries are used as-is; the engine consumes the raw response. */
    resultShape: 'raw',
    /** No profile-level binding: each trigger host declares its own (or none). */
    resultBinding: '',
    outputsLabel: 'Output Variables',
    outputsHint: '',
    /**
     * Label of the composer's explicit change-intent button (sent as promptIntent
     * 'change'); the companion 'Ask a Question' button sends 'ask'. Explicit intent
     * skips the AI's "did you mean a question or a change?" clarifying round, and 'ask'
     * turns are server-enforced to never modify artifacts.
     */
    changeActionLabel: 'Update Outputs',
    /**
     * Props the host editor is expected to provide (mirrors the server profile's
     * ContextSlots). The dialog checks these on open and console-warns when a host
     * stops passing one — the client half of the context contract.
     */
    expectedContext: ['javascriptTemplateCatalog', 'runtimeNotes'],
  },
  customValidation: {
    key: 'customValidation',
    /** Validation queries are SQL over the Api_* views; sent on every test request. */
    queryLanguage: 'sql',
    /** The validation expression is a boolean expression, tested via the expression harness. */
    shapeKind: 'expression',
    /** Custom validation has exactly ONE artifact: the validation expression. */
    allowAddOutputs: false,
    /** The validation query result is materialised as an array of rows. */
    resultShape: 'all',
    /** The executed query result is bound at ctx.query for the expression. */
    resultBinding: 'query',
    outputsLabel: 'Validation expression',
    outputsHint: 'Return true when the record is VALID (the validation passes). Return false to block the stage and show the validation message.',
    changeActionLabel: 'Update Expression',
    /** Polarity/runtime guidance lives in the server profile prompt; no runtimeNotes expected. */
    expectedContext: ['javascriptTemplateCatalog'],
  },
  setVariableFromQuery: {
    key: 'setVariableFromQuery',
    /** Node queries are SQL over the Api_* views; sent on every test request. */
    queryLanguage: 'sql',
    /** The value expression runs in the engine's set-variable harness. */
    shapeKind: 'setVariable',
    /** Exactly ONE artifact besides the queries: the variable value expression. */
    allowAddOutputs: false,
    /** Query results are materialised as arrays of rows. */
    resultShape: 'all',
    /**
     * No profile-level binding: each query binds its own rows at queries.<queryName>
     * (the host derives the path via setVariableFromQueryBindingPath below).
     */
    resultBinding: '',
    /** Users may add, rename, and remove named queries in the session. */
    allowAddQueries: true,
    outputsLabel: 'Variable value expression',
    outputsHint: 'A JavaScript expression that shapes the query results into the variable value. Each query\u2019s rows are available at ctx.queries.<queryName>.',
    changeActionLabel: 'Update Expression',
    expectedContext: ['javascriptTemplateCatalog', 'runtimeNotes'],
  },
  workflowWait: {
    key: 'workflowWait',
    /** Wait queries are SQL over the Api_* views; sent on every test request. */
    queryLanguage: 'sql',
    /** The wait expression is a boolean expression, tested via the expression harness. */
    shapeKind: 'expression',
    /** A wait has exactly ONE artifact besides the query: the wait expression. */
    allowAddOutputs: false,
    /** Default result shape; the host overrides per wait with its configured mode. */
    resultShape: 'all',
    /** The executed query result is bound at ctx.query for the expression. */
    resultBinding: 'query',
    outputsLabel: 'Wait expression',
    outputsHint: 'Return true when the wait condition is MET (the workflow continues). Return false to keep waiting; the engine re-evaluates on its next poll.',
    changeActionLabel: 'Update Expression',
    expectedContext: ['javascriptTemplateCatalog', 'runtimeNotes'],
  },
  workflowNodeQuery: {
    key: 'workflowNodeQuery',
    /** Node queries are SQL over the Api_* views; sent on every test request. */
    queryLanguage: 'sql',
    /**
     * QUERY-ONLY host: the runtime consumes the query result directly (guard truthiness,
     * first row's ID, recipient rows, setting value) with no JavaScript output. The
     * dialog hides the outputs panel and the session authors only the query.
     */
    hasOutputs: false,
    allowAddOutputs: false,
    /** Query results are materialised as arrays of rows for previews. */
    resultShape: 'all',
    resultBinding: '',
    outputsLabel: '',
    outputsHint: '',
    okLabel: 'Use Query',
    chatPlaceholder: 'Describe the rows this query should return...',
    changeActionLabel: 'Update Query',
    /** The host editor MUST state how its runtime consumes the result (runtimeNotes). */
    expectedContext: ['runtimeNotes'],
  },
  adhocReport: {
    key: 'adhocReport',
    /**
     * Output tests run through the CUSTOMIZATION endpoints (web app in-process, gated
     * on customization management) instead of the workflow node-test endpoints —
     * customization authoring must not require the Workflow feature or the workflow
     * engine service to exist.
     */
    nodeTestScope: 'customization',
    /** Report queries are SQL over the Api_* views; sent on every test/refresh request. */
    queryLanguage: 'sql',
    /** The report script evaluates like a set-variable value (any JSON value; here a string). */
    shapeKind: 'setVariable',
    /** Exactly ONE artifact besides the queries: the report HTML script. */
    allowAddOutputs: false,
    /** Query results are materialised as arrays of rows. */
    resultShape: 'all',
    /** Each query binds its own rows at queries.<queryName> (see bindingPath below). */
    resultBinding: '',
    /** Users (and the AI) may plan multi-query reports. */
    allowAddQueries: true,
    outputsLabel: 'Report script',
    outputsHint: 'A JavaScript expression that renders the query results into one complete standalone HTML document (a string). Each query\u2019s rows are available at ctx.queries.<queryName>.',
    /** Standalone host (toolbar): there is no editor behind it to supply catalogs/notes. */
    expectedContext: [],
    /**
     * Profile-owned preview rendered in the dialog's Preview tab: the sandboxed report
     * iframe plus the third-party-libraries disclaimer/consent. Profiles without a
     * previewComponent get DefaultQueryShapingPreview (generic tables/values).
     * The component receives { queries, outputs, busy, sessionOptions, templateValues,
     * onTemplateValuesChange, onSessionOptionsChange, onRefresh, onDefect }; onRefresh
     * accepts an optional template-values patch that the refresh run merges in (the
     * report date-range picker's computed instants).
     */
    previewComponent: AdhocReportPreview,
    /** Reports are consumed visually, so the preview is the tab users land on. */
    defaultTab: 'preview',
    dialogTitle: 'Report Assistant',
    /** Standalone session: nothing is persisted on close, so the OK action just closes. */
    okLabel: 'Done',
    chatPlaceholder: 'Describe the report you want (e.g. "top 10 customers by sales amount as a table and pie chart")...',
    changeActionLabel: 'Update Report',
  },
  dataExport: {
    key: 'dataExport',
    /** Output tests run through the customization endpoints (see adhocReport). */
    nodeTestScope: 'customization',
    /** Sheet queries are SQL over the Api_* views; sent on every test/refresh request. */
    queryLanguage: 'sql',
    /** Mapping scripts evaluate like set-variable values (here an array of row objects). */
    shapeKind: 'setVariable',
    /**
     * Each query (sheet) pairs with ONE same-named mapping output, so adding a sheet
     * means the AI adds a query AND an output — both add capabilities are on.
     */
    allowAddOutputs: true,
    /** Query results are materialised as arrays of rows. */
    resultShape: 'all',
    /** Each query binds its own rows at queries.<queryName> (see bindingPath below). */
    resultBinding: '',
    allowAddQueries: true,
    /**
     * $top is SEMANTIC here (the sheet's row limit, honored by the generated file), so
     * an all-rows sheet has NO $top — the dialog's refresh instead asks the test
     * endpoint to clamp rows for the preview. Mirrors DataExportSessionProfile.MaxPreviewRows (C#).
     */
    previewMaxRows: 200,
    outputsLabel: 'Sheet column mappings',
    outputsHint: 'One mapping per sheet, named after its query: a JavaScript expression that turns ctx.queries.<name> rows into an array of flat objects (keys = column headers, values = cell values).',
    /** Standalone host (toolbar): there is no editor behind it to supply catalogs/notes. */
    expectedContext: [],
    /**
     * Profile-owned preview: one tab per sheet showing the mapped rows as a table, plus
     * the Generate Export button that posts the definition snapshot to the start-export
     * endpoint (a background task delivers the workbook via a download notification).
     */
    previewComponent: DataExportPreview,
    defaultTab: 'preview',
    dialogTitle: 'Data Export Assistant',
    /** Standalone session: nothing is persisted on close, so the OK action just closes. */
    okLabel: 'Done',
    chatPlaceholder: 'Describe the export you want (e.g. "all open jobs with customer, sales rep and amount, plus a payments sheet")...',
    changeActionLabel: 'Update Export',
  },
  dataGrid: {
    key: 'dataGrid',
    /** Output tests run through the customization endpoints (see adhocReport). */
    nodeTestScope: 'customization',
    /** Grid queries are SQL over the Api_* views; sent on every test/page/refresh request. */
    queryLanguage: 'sql',
    /** The grid definition evaluates like a set-variable value (a static JSON object). */
    shapeKind: 'setVariable',
    /** Exactly ONE artifact besides the query: the 'grid' definition output. */
    allowAddOutputs: false,
    /** Query results are materialised as arrays of rows. */
    resultShape: 'all',
    /** Each query binds its own rows at queries.<queryName> (see bindingPath below). */
    resultBinding: '',
    /**
     * The AI may add CHILD queries beyond the fixed 'data' grid query: each backs a
     * { queryName } child grid, filtered per expanded row via {{parent.<Field>}}
     * tokens (how an aggregate main grid still expands into detail rows).
     */
    allowAddQueries: true,
    /**
     * TOP is SEMANTIC here (the grid's total-row cap — the preview pages with the
     * host-composed grid state), so session test runs clamp rows separately. Mirrors
     * DataGridSessionProfile.MaxPreviewRows (C#).
     */
    previewMaxRows: 50,
    outputsLabel: 'Grid definition',
    outputsHint: 'A static object declaring the grid\u2019s columns ({ field, title, picklist?, link?, align?, style?, cellStyles? }) and optional child grids ({ queryName, title, columns, childGrids? } — child grids nest) — every field is a real returned column name so paging and sorting can re-run the query.',
    /** Standalone host (toolbar): there is no editor behind it to supply catalogs/notes. */
    expectedContext: [],
    /**
     * Profile-owned preview: the query's rows rendered live in a paged, sortable HTML
     * grid (grid state is structured — header clicks become gridState.sort, the pager
     * moves gridState.skip; the server composes them around the SQL), with expandable
     * query-backed child grids.
     */
    previewComponent: DataGridPreview,
    /** Grids are consumed visually, so the preview is the tab users land on. */
    defaultTab: 'preview',
    dialogTitle: 'Data Grid Assistant',
    /** Standalone session: nothing is persisted on close, so the OK action just closes. */
    okLabel: 'Done',
    chatPlaceholder: 'Describe the data you want to browse (e.g. "all open jobs with customer and amount, with their job lines")...',
    changeActionLabel: 'Update Grid',
  },
  productChangeReview: {
    key: 'productChangeReview',
    /**
     * CHAT-ONLY profile: the session reviews a product definition change — it authors
     * no queries and no scripts, so the dialog hides both the outputs panel and the
     * query editor (hosts pass previewContent to show the Changes screen instead).
     * Server half: ChangeReviewSessionProfile (C#) with the consultProductSpec skill,
     * grounded by the ProductSpecificationOracle on the generated ProductSpec/
     * PricingSpec documents plus the saved definition (loaded server-side by
     * changeProductID — the client never uploads it).
     */
    hasQueries: false,
    hasOutputs: false,
    allowAddOutputs: false,
    resultShape: 'raw',
    resultBinding: '',
    outputsLabel: '',
    outputsHint: '',
    dialogTitle: 'Product Change Review',
    /**
     * The session's deliverable is the REVIEW CHECKLIST artifact, grouped by LOGICAL
     * CHANGE (related edits serving one editing goal, e.g. creating an option and
     * setting it as a pricing option). Its content is produced server-side from the
     * product specification oracle's assessment (server lane
     * ChecklistSessionArtifactLane, merged by item id) — the user ticks each item
     * Intended/Unintended in the dialog's checklist tab. checklistJson round-trips on
     * every request; user states are preserved server-side across re-assessments.
     */
    hasChecklist: true,
    /**
     * Sent automatically as the session's first turn (intent 'change') when the dialog
     * opens fresh with a diff present, so the review is underway before the user has
     * to ask for anything: the assistant consults the oracle, whose logical-change
     * assessment becomes the checklist in that same turn. Resumed sessions never
     * re-send it.
     */
    autoFirstPrompt: 'Review this change and build the checklist of its effects.',
    /** Review sessions change nothing; closing just dismisses the conversation. */
    okLabel: 'Done',
    chatPlaceholder: 'State what this change is meant to achieve, or ask about its effects...',
    changeActionLabel: 'Review Change',
    /**
     * The host dialog (product editor Changes dialog) must provide the computed diff of
     * the edit (changeDiffJson, DiffViewer path grammar) and the product id
     * (productID); the intent is typed by the user in chat. Mirrors the server
     * profile's ContextSlots.
     */
    expectedContext: ['changeDiffJson', 'productID'],
  },
  productExplainer: {
    key: 'productExplainer',
    /**
     * CHAT-ONLY profile: diagnostic questions about a configured job line ("why can't
     * I see option X when I select Y?", "why is option Z priced at $20?"). Hosted by
     * the job line editor (JobLineView) and the price details line rows
     * (JobPriceDetailsCtrl) via the AI wand; both snapshot the line(s) — identity by
     * name (product name/code/version, supplier, multi-config membership),
     * dimensions, chosen options and PriceModel — as explainContextJson (see
     * buildJobLineExplainContext), and the server loads the actual definitions by the
     * ids inside it. Server half: ProductExplainerSessionProfile (C#) with the
     * consultProductSpec skill.
     */
    hasQueries: false,
    hasOutputs: false,
    allowAddOutputs: false,
    resultShape: 'raw',
    resultBinding: '',
    outputsLabel: '',
    outputsHint: '',
    dialogTitle: 'Product & Pricing Assistant',
    /** Explainer sessions change nothing; closing just dismisses the conversation. */
    okLabel: 'Done',
    chatPlaceholder: 'Ask about this configuration (e.g. "why can\u2019t I see option X when I select Y?" or "why is option Z priced at $20?")...',
    /** The host must snapshot the line configuration being asked about. */
    expectedContext: ['explainContextJson'],
  },
};

/**
 * User-feature gate for the product explainer wand (productExplainer) on the job line
 * editor (JobLineView) and the price details line rows (JobPriceDetailsCtrl) — the
 * change review (productChangeReview) is generally released and does not check this.
 * Granted per representative via FeatureAllowProductExplainer (Representative editor →
 * features); Insyte support users always pass, mirroring the server. Presentation-only:
 * the server re-checks the same feature on every productExplainer chat turn
 * (Workflow_QueryShaping_Chat).
 */
export const productExplainerEnabled = () => {
  try {
    const access = getAccess();
    if (!access) return false;
    if (getUser()?.IsSupportUser) return true;
    return !!access.features?.().FeatureAllowProductExplainer?.();
  } catch {
    return false;
  }
};

/**
 * The ctx path a Set Variable (Query) query's rows are bound at. Mirrors
 * SetVariableFromQueryQueries.BindingPath (C#) so dialog tests, engine tests, and run-time
 * execution all bind identically.
 */
export const setVariableFromQueryBindingPath = (queryName) => `queries.${(queryName ?? '').toString().trim()}`;

/**
 * Resolves a profile by key, falling back to the default profile for unknown keys —
 * mirroring the server's ResolveSessionProfile so client and server always degrade the
 * same way.
 */
export const resolveQueryShapingProfile = (key) => (
  queryShapingSessionProfiles[key] ?? queryShapingSessionProfiles[QUERY_SHAPING_DEFAULT_PROFILE]
);
